/**
 * Zipchat renders its product search inside an open shadow root. Use prices
 * rendered by Liquid so its results match the rest of the storefront.
 */
const script = document.querySelector('script[data-clearance-search-prices]');
const collectionUrl = script?.dataset.collectionUrl;

if (collectionUrl) {
  const resultSelector = 'a.zipchat-search-product[data-zipchat-search-result]';
  const priceSelector = '.zipchat-search-product-price';
  const parser = new DOMParser();
  const pagePromises = new Map();
  let pricesPromise;
  let shadowRoot;
  let refreshPending = false;

  const shadowObserver = new MutationObserver(scheduleRefresh);
  const shadowObserverOptions = {
    childList: true,
    subtree: true,
    characterData: true,
    attributes: true,
    attributeFilter: ['href', 'data-zipchat-atc-handle'],
  };

  /** Fetch each collection page once; unsuccessful requests can be retried. */
  function loadPage(page) {
    if (!pagePromises.has(page)) {
      const url = new URL(collectionUrl, window.location.origin);
      if (url.origin !== window.location.origin) return Promise.reject(new Error('Invalid collection origin'));
      url.searchParams.set('section_id', 'clearance-search-prices');
      url.searchParams.set('page', String(page));
      const previewTheme = new URL(window.location.href).searchParams.get('preview_theme_id');
      if (previewTheme) url.searchParams.set('preview_theme_id', previewTheme);

      // Bound both the request and response body read so refreshes cannot stay locked.
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 10000);
      const promise = fetch(url, { credentials: 'same-origin', signal: controller.signal })
        .then(async (response) => {
          if (!response.ok) throw new Error('Collection prices unavailable');
          const html = parser.parseFromString(await response.text(), 'text/html');
          const payload = html.querySelector('[data-clearance-search-payload]') || html.querySelector('.shopify-section');
          const data = JSON.parse(payload?.textContent || '');
          if (!Array.isArray(data.products)) throw new Error('Invalid collection prices');
          if (data.next_page !== null && (!Number.isInteger(data.next_page) || data.next_page <= page)) {
            throw new Error('Invalid collection pagination');
          }
          return data;
        })
        .catch((error) => {
          pagePromises.delete(page);
          throw error;
        })
        .finally(() => clearTimeout(timeout));
      pagePromises.set(page, promise);
    }
    return pagePromises.get(page);
  }

  /** Build one lookup lazily, only after the widget has product results. */
  function loadPrices() {
    if (!pricesPromise) {
      pricesPromise = (async () => {
        const prices = new Map();
        let page = 1;
        while (page !== null) {
          const data = await loadPage(page);
          for (const product of data.products) {
            if (typeof product.handle !== 'string' || typeof product.price_html !== 'string') continue;
            const html = parser.parseFromString(product.price_html, 'text/html');
            const sale = html.querySelector('.price')?.textContent?.trim();
            const regular = html.querySelector('.compare-at-price')?.textContent?.trim();
            const badge = html.querySelector('[data-clearance-badge]')?.textContent?.trim();
            if (sale && regular && badge) prices.set(product.handle, { sale, regular, badge });
          }
          page = data.next_page;
        }
        return prices;
      })().catch((error) => {
        pricesPromise = undefined;
        throw error;
      });
    }
    return pricesPromise;
  }

  function productHandle(result) {
    const handle = result.querySelector('[data-zipchat-atc-handle]')?.dataset.zipchatAtcHandle;
    if (handle) return handle;
    try {
      const path = new URL(result.href, window.location.origin).pathname;
      return decodeURIComponent(path.match(/\/products\/([^/]+)\/?$/)?.[1] || '');
    } catch {
      return '';
    }
  }

  function updatePrice(container, price) {
    const regular = container.querySelector('[data-clearance-search-regular]');
    const sale = container.querySelector('[data-clearance-search-sale]');
    const badge = container.querySelector('[data-clearance-badge]');
    if (
      regular?.textContent === price.regular &&
      sale?.textContent === price.sale &&
      badge?.textContent === price.badge &&
      container.children.length === 3
    ) {
      return;
    }

    const regularNode = document.createElement('s');
    regularNode.dataset.clearanceSearchRegular = '';
    regularNode.className = 'zipchat-search-product-price-regular';
    regularNode.textContent = price.regular;
    regularNode.style.cssText = 'font-size:.9em;opacity:.65;text-decoration:line-through;';

    const saleNode = document.createElement('span');
    saleNode.dataset.clearanceSearchSale = '';
    saleNode.className = 'zipchat-search-product-price-sale';
    saleNode.textContent = price.sale;
    saleNode.style.fontWeight = '600';

    const badgeNode = document.createElement('span');
    badgeNode.dataset.clearanceBadge = '';
    badgeNode.textContent = price.badge;
    badgeNode.style.cssText =
      'display:inline-flex;align-items:center;padding:2px 6px;border:1px solid #ddd;border-radius:999px;' +
      'font-size:11px;font-weight:700;line-height:1.2;color:#222;background:#fff;white-space:nowrap;';

    Object.assign(container.style, { display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' });
    container.replaceChildren(regularNode, saleNode, badgeNode);
  }

  function scheduleRefresh() {
    if (refreshPending) return;
    refreshPending = true;
    queueMicrotask(async () => {
      try {
        if (!shadowRoot?.querySelector(resultSelector)) return;
        const prices = await loadPrices();
        if (!shadowRoot?.host.isConnected) return;
        // Our mutations must not start another fetch/render cycle.
        shadowObserver.disconnect();
        for (const result of shadowRoot.querySelectorAll(resultSelector)) {
          const price = prices.get(productHandle(result));
          const container = result.querySelector(priceSelector);
          if (price && container) updatePrice(container, price);
        }
      } catch {
        // Leave vendor prices intact on failure. A later widget mutation can retry.
      } finally {
        if (shadowRoot?.host.isConnected) shadowObserver.observe(shadowRoot, shadowObserverOptions);
        refreshPending = false;
      }
    });
  }

  function connectWidget() {
    const root = document.getElementById('zipchat-shadow-host')?.shadowRoot;
    if (root === shadowRoot) return;
    shadowObserver.disconnect();
    shadowRoot = root;
    if (shadowRoot) {
      shadowObserver.observe(shadowRoot, shadowObserverOptions);
      scheduleRefresh();
    }
  }

  // The app can create or replace its host after the theme modules have loaded.
  const bodyObserver = new MutationObserver(connectWidget);
  bodyObserver.observe(document.body, { childList: true, subtree: true });
  // Also detect a shadow root attached after its host was inserted, when search opens.
  document.addEventListener('focusin', connectWidget, true);
  document.addEventListener('input', connectWidget, true);
  document.addEventListener('click', connectWidget, true);
  connectWidget();
}
